import { SharedAngular } from '@Client/@types/sharedAngular';
import ISettingDetail from '@Shared.Angular/@types/core/contracts/config/ISettingDetail';
import ISettingOverrideDetail from '@Shared.Angular/@types/core/contracts/config/ISettingOverrideDetail';
import ISettingTagDetail from '@Shared.Angular/@types/core/contracts/config/ISettingTagDetail';
import { IConfirmDialogOptions } from '@Shared.Angular/flowingly.services/dialog.service';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import angular, { IScope } from 'angular';
import { IStateProvider } from 'angular-ui-router';

class ApplicationSettingsController {
  settingTypes: string[] = ['Application', 'Business', 'User'];
  hasActionsPermission: boolean;
  settings: ISettingDetail[] = [];
  setting: ISettingDetail = { value: '', tags: [] } as ISettingDetail;
  tagNames: string[] = [];
  tagFilter?: string;
  loading = false;
  deletedOverrides: ISettingOverrideDetail[] = [];
  newTag: string | null = null;
  selectedTag: string | null = null;

  private scrollOptions: ScrollIntoViewOptions = {
    behavior: 'smooth',
    block: 'center'
  };

  static $inject = [
    '$scope',
    'maintenanceService',
    'dialogService',
    'notificationService',
    'permissionsService',
    'fileService'
  ];

  constructor(
    private $scope: IScope,
    private maintenanceService: MaintenanceService,
    private dialogService: SharedAngular.DialogService,
    private notificationService: SharedAngular.NotificationService,
    private permissionsService: SharedAngular.PermissionsService,
    private fileService: SharedAngular.FileService
  ) {
    this.hasActionsPermission =
      this.permissionsService.currentUserHasAnyOfPermissions([
        FlowinglyPermissions.ADMIN_ACTIONS,
        FlowinglyPermissions.ADMIN_ACTIONS_APPLICATION_SETTING
      ]);

    this.loadSettings();
  }

  loadSettings(): void {
    this.loading = true;
    this.maintenanceService.getApplicationSettings().then((settings) => {
      this.settings = settings.sort((a, b) => a.name.localeCompare(b.name));
      this.tagNames = Array.from(
        new Set(settings.flatMap((setting) => setting.tags.map((t) => t.name)))
      ).sort();
      this.setting = { value: '', tags: [] } as ISettingDetail;
      this.deletedOverrides = [];
      this.loading = false;
    });
  }

  clearCachedSettings(): void {
    this.maintenanceService.clearCachedSettings().then((wasSuccess) => {
      if (wasSuccess) {
        this.notificationService.showSuccessToast('Cached Settings cleared.');
      } else {
        this.notificationService.showErrorToast(
          'Error clearing cached Settings.'
        );
      }
    });
  }

  downloadOverrideReport(): void {
    const reportSettings = this.settings
      .filter((s) => s.type === 'Business')
      .flatMap((s) => {
        return [[s.name, 'DEFAULT VALUE', s.value || '']]
          .concat(
            s.overrides
              .filter((o) => o.value !== s.value)
              .sort((a, b) => a.businessName.localeCompare(b.businessName))
              .map((o) => [
                s.name,
                o.businessName,
                o.value.length < 200
                  ? o.value || ''
                  : '<Value omitted for length>'
              ])
          )
          .concat([['', '', '']]);
      });
    reportSettings.unshift([
      'Setting Name',
      'Business overriding DEFAULT VALUE',
      'Value'
    ]);
    const csvContent = reportSettings
      .map((row) =>
        row.map((column) => `"${column.replace(/"/g, '""')}"`).join(',')
      )
      .join('\n');
    const utf8Bytes = new TextEncoder().encode(csvContent);
    const utf8String = Array.from(utf8Bytes)
      .map((byte) => String.fromCharCode(byte))
      .join('');
    this.fileService.download(
      btoa(utf8String),
      'application_settings_overrides_report.csv',
      'text/csv'
    );
  }

  editSetting(setting: ISettingDetail): void {
    this.setting = angular.copy(setting);
    this.deletedOverrides = [];
    const settingValueInput = document.getElementById(
      'setting-value-input'
    ) as HTMLInputElement;
    settingValueInput.scrollIntoView(this.scrollOptions);
    settingValueInput.select();
  }

  updateSetting(setting: ISettingDetail): void {
    this.loading = true;
    const dialogOptions: IConfirmDialogOptions = {
      title: 'Add/Update Application Setting?',
      message:
        'Application Settings affect all Businesses that do not have an overriding Business Setting, are you sure?'
    };
    this.dialogService
      .showConfirmDialog(this.$scope, dialogOptions)
      .then(() => {
        return this.maintenanceService.setApplicationSetting(setting);
      })
      .then(() => {
        this.notificationService.showSuccessToast(
          `Application Setting '${this.setting.name}' was updated.`
        );
        this.loadSettings();
      })
      .catch((e) => {
        this.loading = false;
        if (e?.status && !e?.data?.message) {
          this.notificationService.showErrorToast(
            `Unexpected error occurred updating '${this.setting.name}'`
          );
        }
      });
  }

  deleteSetting(settingName: string): void {
    this.loading = true;
    const dialogOptions: IConfirmDialogOptions = {
      title: `Delete '${settingName}'?`,
      message:
        'Application Settings must not be deleted if there is any code still referencing them. ' +
        (this.setting.type === 'Business'
          ? 'If there are any overriding Business Settings they will be deleted too. '
          : '') +
        'Are you sure?',
      actionButtonClass: 'red',
      confirmationWord: 'DELETE',
      disabledSeconds: 5
    };
    this.dialogService
      .showConfirmDialog(this.$scope, dialogOptions)
      .then(() => {
        return this.maintenanceService.deleteApplicationSetting(settingName);
      })
      .then(() => {
        this.notificationService.showSuccessToast(
          `Application Setting '${this.setting.name}' was deleted.`
        );
        this.loadSettings();
      })
      .catch((e) => {
        this.loading = false;
        if (e?.status && !e?.data?.message) {
          this.notificationService.showErrorToast(
            `Unexpected error occurred updating '${this.setting.name}'`
          );
        }
      });
  }

  deleteOverride(override: ISettingOverrideDetail): void {
    const overrideIndex = this.setting.overrides.findIndex(
      (o) => o.id === override.id
    );
    const deletedOverride = this.setting.overrides.splice(overrideIndex, 1);
    this.deletedOverrides.push(...deletedOverride);
  }

  restoreOverride(override: ISettingOverrideDetail): void {
    const overrideIndex = this.deletedOverrides.findIndex(
      (o) => o.id === override.id
    );
    const restoredOverride = this.deletedOverrides.splice(overrideIndex, 1);
    this.setting.overrides.push(...restoredOverride);
  }

  addTag(tagName: string): void {
    if (this.setting.tags.some((t) => t.name === tagName)) {
      return;
    }
    this.setting.tags.push({ name: tagName, description: '' });
  }

  removeTag(tag: ISettingTagDetail): void {
    this.setting.tags = this.setting.tags.filter((t) => t !== tag);
  }

  replaceTag(tag: string, newTag: string): void {
    if (
      typeof newTag !== 'string' ||
      this.setting.tags.some((t) => t.name === newTag)
    ) {
      return;
    }
    const index = this.setting.tags.findIndex((t) => t.name === tag);
    if (index >= 0) {
      this.setting.tags[index] = { name: newTag, description: '' };
      this.newTag = null;
      this.selectedTag = null;
    }
  }

  filterByTag = (setting: ISettingDetail): boolean => {
    return (
      !this.tagFilter ||
      setting.tags.some((tag) => tag.name.indexOf(this.tagFilter) > -1)
    );
  };
}

angular.module('flowingly.runner.maintenance').config(config);

config.$inject = ['$stateProvider'];

function config($stateProvider: IStateProvider) {
  $stateProvider.state(STATE_MAPPINGS.maintenanceApplicationSettings, {
    url: '/applicationsettings',
    params: { title: 'Maintenance - Application Settings' },
    views: {
      childcontent: {
        templateUrl:
          'Client/runner.maintenance/applicationsettings/runner.maintenance.applicationsettings.tmpl.html',
        controllerAs: '$ctrl',
        controller: ApplicationSettingsController
      }
    }
  });
}
